import { FileText, Search, Grid, List, Star, Heart, Eye, ShoppingCart, ArrowRight, Leaf, Shield, Truck, Award } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

const ProductImage = ({ src, alt, className }) => {
  const isBase64 = (str) =>
    typeof str === "string" && str.startsWith("data:");

  const isPDF = (str) =>
    typeof str === "string" &&
    (str.endsWith(".pdf") || str.includes("application/pdf"));

  const getFullUrl = (path) => {
    if (typeof path === "string" && path.startsWith("/")) {
      return `${window.location.origin}${path}`;
    }
    return path;
  };

  if (!src) {
    return (
      <img
        src="/placeholder-image.jpg"
        alt="placeholder"
        className={className}
      />
    );
  }

  if (isPDF(src)) {
    const pdfUrl = getFullUrl(src);
    return (
      <div className="flex flex-col items-center justify-center h-full w-full bg-gray-100 rounded">
        <FileText size={40} className="text-red-600 mb-2" />
        <span className="text-sm text-gray-600 mb-1">{alt || "Document PDF"}</span>
        <a
          href={pdfUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="text-sm text-blue-600 hover:underline"
        >
          Voir le PDF
        </a>
      </div>
    );
  }

  const imageUrl = isBase64(src) || (typeof src === "string" && src.startsWith("http"))
    ? src
    : getFullUrl(src);

  return (
    <img
      src={imageUrl}
      alt={alt}
      className={className}
      onError={(e) => {
        e.target.src = "/placeholder-image.jpg";
        e.target.onerror = null;
      }}
    />
  );
};

const ProductsPage = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [minPrice, setMinPrice] = useState("0.00");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hoveredProduct, setHoveredProduct] = useState(null);
  const [viewMode, setViewMode] = useState('grid'); // 'grid' or 'list'
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'price', 'newest'

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage] = useState(9);

  // Helper function to format price by removing trailing zeros
  const formatPrice = (price) => {
    // Convert to string first
    const priceStr = price.toString();
    
    // If it's not a valid number, return as is
    if (isNaN(parseFloat(priceStr))) {
      return priceStr;
    }
    
    // Parse as number to remove trailing zeros, then convert back to string
    const numPrice = parseFloat(priceStr);
    
    // If it's a whole number, add .00
    if (numPrice % 1 === 0) {
      return numPrice.toString() + '.00';
    }
    
    // For decimal numbers, parseFloat automatically removes trailing zeros
    return numPrice.toString();
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch products and sizes in parallel
        const [productsResponse, sizesResponse] = await Promise.all([
          fetch("https://api.greenecobagsusa.com/api/products"),
          fetch("https://api.greenecobagsusa.com/api/sizes")
        ]);

        if (!productsResponse.ok || !sizesResponse.ok) {
          throw new Error("Failed to fetch data");
        }

        const productsData = await productsResponse.json();
        const sizesData = await sizesResponse.json();

        // Find the minimum price from all sizes
        let lowestPrice = Number.MAX_VALUE;
        if (Array.isArray(sizesData)) {
          sizesData.forEach(size => {
            const price = parseFloat(size.price);
            if (!isNaN(price) && price < lowestPrice) {
              lowestPrice = price;
            }
          });
        } else if (sizesData.data && Array.isArray(sizesData.data)) {
          sizesData.data.forEach(size => {
            const price = parseFloat(size.price);
            if (!isNaN(price) && price < lowestPrice) {
              lowestPrice = price;
            }
          });
        }

        // Set the global minimum price
        const globalMinPrice = lowestPrice !== Number.MAX_VALUE ? lowestPrice.toFixed(2) : "0.00";
        setMinPrice(globalMinPrice);

        // Check and process product data
        let productsArray = [];
        if (Array.isArray(productsData)) {
          productsArray = productsData;
        } else if (productsData.data && Array.isArray(productsData.data)) {
          productsArray = productsData.data;
        } else {
          console.error("Unexpected product data format:", productsData);
          throw new Error("Incorrect product data format");
        }

        setProducts(productsArray);
        setIsVisible(true);
      } catch (err) {
        setError(err.message);
        console.error("Error fetching data:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleProductClick = (id) => {
    navigate(`/details/${id}`);
  };

  // Filter and sort products
  const filteredProducts = products.filter(product =>
    product.name && product.name.toLowerCase().includes(searchTerm.toLowerCase())
  );
  

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    switch (sortBy) {
      case 'name':
        return a.name.localeCompare(b.name);
      case 'price':
        return parseFloat(minPrice) - parseFloat(minPrice);
      case 'newest':
        return new Date(b.created_at || 0) - new Date(a.created_at || 0);
      default:
        return 0;
    }
  });

  // Get current products based on pagination
  const indexOfLastProduct = currentPage * productsPerPage;
  const currentProducts = sortedProducts.slice(0, indexOfLastProduct);

  // Function to load more products
  const loadMoreProducts = () => {
    setCurrentPage(currentPage + 1);
  };

  const features = [
    { icon: <Shield className="w-6 h-6" />, text: "Quality Guaranteed" },
    { icon: <Leaf className="w-6 h-6" />, text: "100% Eco-Friendly" },
    { icon: <Truck className="w-6 h-6" />, text: "Fast Delivery" },
    { icon: <Award className="w-6 h-6" />, text: "Custom Branding" }
  ];

  if (loading) {
    return (
      <section className="w-full py-20 mt-20">
        <div className="container mx-auto px-4">
          <div className="flex flex-col items-center space-y-6">
            <div className="relative">
              <div className="animate-spin h-16 w-16 rounded-full border-4 border-green-200 border-t-green-600 mx-auto"></div>
              <div className="absolute inset-0 animate-ping h-16 w-16 rounded-full border-4 border-green-300 opacity-20"></div>
            </div>
            <div className="space-y-2">
              <h3 className="text-xl font-semibold text-gray-700">Loading Amazing Products</h3>
              <p className="text-gray-500">Discovering the best eco-friendly options for you...</p>
            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="w-full py-20 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-10 h-10 bg-red-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xl">!</span>
              </div>
          </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">Oops! Something went wrong</h3>
            <p className="text-gray-600 mb-8">{error}</p>
          <button
            onClick={() => window.location.reload()}
              className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-3 rounded-full font-semibold transition-all duration-300 transform hover:scale-105 hover:shadow-lg"
          >
              Try Again
          </button>
          </div>
        </div>
      </section>
    );
  }

  if (!products || products.length === 0) {
    return (
      <section className="w-full py-20 mt-20">
        <div className="container mx-auto px-4">
          <div className="max-w-md mx-auto">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Leaf className="w-10 h-10 text-gray-400" />
            </div>
            <h3 className="text-2xl font-bold text-gray-800 mb-4">No Products Available</h3>
            <p className="text-gray-600">We're working on adding amazing eco-friendly products. Check back soon!</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="relative w-full py-20 mt-20 overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-10 left-10 w-32 h-32 bg-green-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-10 right-10 w-40 h-40 bg-emerald-100 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-100 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      <div className="container mx-auto px-4">
        {/* Header Section */}
        <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="inline-flex items-center space-x-2 bg-green-100 text-green-800 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Leaf className="w-4 h-4" />
            <span>Eco-Friendly Products</span>
          </div>
          
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            All Our
            <span className="block bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 bg-clip-text text-transparent">
              Sustainable Products
            </span>
          </h2>
          
          <div className="w-24 h-1 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full mx-auto mb-8"></div>
          
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Discover our complete collection of eco-friendly products that combine 
            sustainability with exceptional quality and style.
          </p>

          {/* Features */}
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {features.map((feature, index) => (
              <div key={index} className="flex items-center space-x-2 bg-white/80 backdrop-blur-sm px-4 py-2 rounded-full shadow-sm">
                <div className="text-green-600">{feature.icon}</div>
                <span className="text-sm font-medium text-gray-700">{feature.text}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Search and Filter Bar */}
        <div className={`mb-12 transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl p-6 shadow-lg border border-white/20">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search products..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                />
              </div>

              {/* Sort Dropdown */}
              <div className="flex items-center space-x-4">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="px-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-green-500 focus:border-transparent transition-all duration-300"
                >
                  <option value="name">Sort by Name</option>
                  <option value="price">Sort by Price</option>
                  <option value="newest">Sort by Newest</option>
                </select>

                {/* View Mode Toggle */}
                <div className="flex bg-gray-100 rounded-xl p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 rounded-lg transition-all duration-300 ${viewMode === 'grid' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  >
                    <Grid className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 rounded-lg transition-all duration-300 ${viewMode === 'list' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                  >
                    <List className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Products Grid */}
        <div className={`grid ${viewMode === 'grid' ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3' : 'grid-cols-1'} gap-8 lg:gap-12 mb-16 transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          {currentProducts.map((product, index) => (
            <div
              key={product.id}
              className={`group relative bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 overflow-hidden cursor-pointer ${viewMode === 'list' ? 'flex' : ''}`}
              onClick={() => handleProductClick(product.id)}
              onMouseEnter={() => setHoveredProduct(product.id)}
              onMouseLeave={() => setHoveredProduct(null)}
              style={{
                animationDelay: `${index * 100}ms`,
                animation: 'fadeInUp 0.6s ease-out forwards'
              }}
            >
              {/* Product Image */}
              <div className={`relative ${viewMode === 'list' ? 'w-1/3 h-48' : 'h-64'} overflow-hidden`}>
                <ProductImage
                  src={product.image_base64}
                  alt={product.name}
                  className="w-full h-full object-cover object-center transform group-hover:scale-110 transition-transform duration-700"
                />
                
                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                
                {/* Action Buttons */}
                <div className="absolute top-4 right-4 flex flex-col space-y-2 opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-x-4 group-hover:translate-x-0">
                  <button className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110">
                    <Heart className="w-5 h-5 text-gray-600 hover:text-red-500" />
                  </button>
                  <button className="w-10 h-10 bg-white/90 hover:bg-white rounded-full flex items-center justify-center shadow-lg transition-all duration-300 hover:scale-110">
                    <Eye className="w-5 h-5 text-gray-600 hover:text-green-500" />
                  </button>
                </div>

                {/* Eco Badge */}
                <div className="absolute top-4 left-4">
                  <div className="bg-green-500 text-white px-3 py-1 rounded-full text-xs font-semibold flex items-center space-x-1">
                    <Leaf className="w-3 h-3" />
                    <span>Eco</span>
                  </div>
                </div>
              </div>

              {/* Product Info */}
              <div className={`p-6 ${viewMode === 'list' ? 'flex-1' : ''}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                    ))}
                  </div>
                  <span className="text-sm text-gray-500">(4.9)</span>
                </div>

                <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-green-600 transition-colors duration-300">
                  {product.name}
                </h3>

                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-baseline space-x-2">
                    <span className="text-2xl font-bold text-green-600">${formatPrice(minPrice)}</span>
                    <span className="text-sm text-gray-500">starting from</span>
                  </div>
              </div>

                <button className="w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white py-3 px-6 rounded-xl font-semibold transition-all duration-300 transform group-hover:scale-105 flex items-center justify-center space-x-2">
                  <span>View Details</span>
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
                </button>
              </div>

              {/* Hover Effect Border */}
              <div className="absolute inset-0 border-2 border-transparent group-hover:border-green-200 rounded-2xl transition-colors duration-300 pointer-events-none"></div>
            </div>
          ))}
        </div>

        {/* Load More Button */}
        {indexOfLastProduct < sortedProducts.length && (
          <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
            <button
              onClick={loadMoreProducts}
              className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
              <div className="flex items-center space-x-3 relative z-10">
                <ShoppingCart className="w-5 h-5" />
                <span>Load More Products</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
            </button>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </section>
  );
};

export default ProductsPage;